import { useState, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronDown, ChevronUp, Calendar, Heart, Sun, Moon, Coffee } from 'lucide-react';

// Import custom hooks
import { useLifeStats } from './hooks/useLifeStats';

// Import animation utilities
import { springConfig, staggerContainer, staggerChild } from './utils/animations';

// Import custom components
import Card from './components/Card';
import Button from './components/Button';
import StatDisplay from './components/StatDisplay';
import ViewSelector from './components/ui/ViewSelector';
import InputSection from './components/layout/InputSection';
import ThemeToggle from './components/ui/ThemeToggle';
import CosmicBackground from './components/CosmicBackground';
import { CelebrationManager } from './components/Celebrations';

// Import visualizations
import WeeksGrid from './components/visualizations/WeeksGrid';
import SeasonsView from './components/visualizations/SeasonsView';
import YearsGrid from './components/visualizations/YearsGrid';
import ProgressView from './components/visualizations/ProgressView';
import CalendarView from './components/visualizations/CalendarView';
import BookView from './components/visualizations/BookView';
import HeartbeatsView from './components/visualizations/HeartbeatsView';
import CosmicView from './components/visualizations/CosmicView';
import DailyView from './components/visualizations/DailyView';
import FunctionHealthLink from './components/FunctionHealthLink';

// View order for determining slide direction
const viewOrder = ['weeks', 'years', 'seasons', 'progress', 'calendar', 'book', 'heartbeats', 'daily', 'cosmic'];

const App = () => {
  // User inputs
  const [birthdate, setBirthdate] = useState("");
  const [sex, setSex] = useState("female");
  const [showDetails, setShowDetails] = useState(false);

  // Visualization state
  const [view, setView] = useState("weeks");
  const [prevView, setPrevView] = useState("weeks");

  // Reduced motion preference
  const prefersReducedMotion = useReducedMotion();

  // Use our custom hook to get life statistics
  const { stats, getCurrentSeason } = useLifeStats(birthdate, sex);
  const currentSeason = getCurrentSeason();

  // Calculate days lived for celebrations
  const daysLived = useMemo(() => {
    if (!birthdate) return 0;
    const birth = new Date(birthdate);
    const today = new Date();
    return Math.floor((today.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24));
  }, [birthdate]);

  // Handle view change with direction tracking
  const handleViewChange = (newView: string) => {
    setPrevView(view);
    setView(newView);
  };

  // Determine slide direction based on view change
  const getSlideDirection = () => {
    const prevIndex = viewOrder.indexOf(prevView);
    const currentIndex = viewOrder.indexOf(view);
    return currentIndex > prevIndex ? 1 : -1;
  };

  // Page transition variants
  const pageVariants = {
    initial: (direction: number) => ({
      opacity: 0,
      x: prefersReducedMotion ? 0 : direction * 50,
      scale: prefersReducedMotion ? 1 : 0.98
    }),
    animate: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number]
      }
    },
    exit: (direction: number) => ({
      opacity: 0,
      x: prefersReducedMotion ? 0 : direction * -50,
      scale: prefersReducedMotion ? 1 : 0.98,
      transition: {
        duration: 0.2
      }
    })
  };

  // Header animation variants
  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        ...springConfig.gentle,
        delay: 0.1
      }
    }
  };

  // Render selected view
  const renderView = () => {
    switch (view) {
      case 'seasons':
        return <SeasonsView stats={stats} />;
      case 'years':
        return <YearsGrid stats={stats} />;
      case 'progress':
        return <ProgressView stats={stats} />;
      case 'calendar':
        return <CalendarView stats={stats} />;
      case 'book':
        return <BookView stats={stats} />;
      case 'heartbeats':
        return <HeartbeatsView stats={stats} />;
      case 'cosmic':
        return <CosmicView stats={stats} />;
      case 'daily':
        return <DailyView stats={stats} />;
      case 'weeks':
      default:
        return <WeeksGrid stats={stats} />;
    }
  };

  return (
    <>
      <CosmicBackground intensity={1} showNebula={true} enableShootingStars={true} />

      {/* Celebration Manager */}
      {birthdate && (
        <CelebrationManager
          daysLived={daysLived}
          weeksLived={stats.weeksLived}
          currentSeason={stats.currentSeason}
          birthdate={birthdate}
        />
      )}

      <div className="relative z-10 min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-catppuccin-base dark:via-catppuccin-mantle dark:to-catppuccin-crust dark:text-catppuccin-text px-4 py-8 font-sans transition-colors duration-200">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.header
            className="text-center mb-10 relative"
            variants={headerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="absolute right-0 top-0">
              <ThemeToggle />
            </div>
            <motion.h1
              className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 dark:from-catppuccin-sapphire dark:to-catppuccin-mauve mb-3 dark:drop-shadow-[0_0_3px_rgba(203,166,247,0.1)]"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...springConfig.bouncy, delay: 0.2 }}
            >
              Mortal Math
            </motion.h1>
            <motion.p
              className="text-gray-600 dark:text-catppuccin-text dark:font-medium text-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Visualize your journey through time.
            </motion.p>
          </motion.header>

          {/* Input Section */}
          <InputSection
            birthdate={birthdate}
            setBirthdate={setBirthdate}
            sex={sex}
            setSex={setSex}
          />

          <AnimatePresence mode="wait">
            {birthdate && (
              <motion.div
                key="content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={springConfig.gentle}
              >
                {/* Quick Stats */}
                <Card className="mb-8 p-6 dark:bg-catppuccin-surface0 dark:border-catppuccin-surface1" animated={true}>
                  <div className="flex justify-between items-center mb-4">
                    <motion.h2
                      className="text-xl font-bold text-gray-800 dark:text-catppuccin-text dark:font-extrabold"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      Life Summary
                    </motion.h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      iconPosition="right"
                      onClick={() => setShowDetails(!showDetails)}
                    >
                      {showDetails ? "Less" : "More"}
                    </Button>
                  </div>

                  <motion.div
                    className="flex flex-wrap gap-2 mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {currentSeason && (
                      <motion.div
                        className="flex items-center gap-2 bg-gray-100 dark:bg-catppuccin-surface2 p-2 rounded-full text-sm dark:font-medium"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className={`p-1 rounded-full bg-${currentSeason.color}-100 text-${currentSeason.color}-500 dark:bg-catppuccin-surface1 dark:text-catppuccin-${currentSeason.color === 'blue' ? 'blue' : currentSeason.color === 'green' ? 'green' : currentSeason.color === 'yellow' ? 'yellow' : 'peach'}`}>
                          <currentSeason.icon size={16} />
                        </div>
                        <span className="dark:text-catppuccin-text">
                          {currentSeason.name} of Life: {currentSeason.description}
                        </span>
                      </motion.div>
                    )}
                  </motion.div>

                  <motion.div
                    className="grid grid-cols-2 md:grid-cols-4 gap-4"
                    variants={staggerContainer(0.1, 0.2)}
                    initial="hidden"
                    animate="visible"
                  >
                    <motion.div variants={staggerChild}>
                      <StatDisplay
                        icon={Calendar}
                        value={`${stats.yearsLived.toLocaleString()} / ${stats.lifeExpectancy.toLocaleString()}`}
                        label="Years"
                        animated={true}
                      />
                    </motion.div>
                    <motion.div variants={staggerChild}>
                      <StatDisplay
                        icon={Calendar}
                        value={`${stats.progressPercent.toFixed(1)}%`}
                        label="Journey Complete"
                        animated={true}
                      />
                    </motion.div>
                    <motion.div variants={staggerChild}>
                      <StatDisplay
                        icon={Sun}
                        value={stats.sunrisesSeen.toLocaleString()}
                        label="Sunrises Seen"
                        color="yellow"
                        animated={true}
                      />
                    </motion.div>
                    <motion.div variants={staggerChild}>
                      <StatDisplay
                        icon={Heart}
                        value={`${(stats.heartbeatsLived / 1000000000).toFixed(2)}B`}
                        label="Heartbeats"
                        color="red"
                        animated={true}
                      />
                    </motion.div>
                  </motion.div>

                  {/* Subtle health nudge */}
                  <motion.div
                    className="mt-4 pt-3 border-t border-gray-100 dark:border-catppuccin-surface2 flex justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <FunctionHealthLink
                      text="Know your real numbers"
                      showIcon={true}
                      showDisclosure={true}
                    />
                  </motion.div>

                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        className="mt-4 pt-4 border-t border-gray-100 dark:border-catppuccin-surface2 grid grid-cols-2 md:grid-cols-4 gap-4"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <StatDisplay
                          icon={Moon}
                          value={stats.moonPhasesLived.toLocaleString()}
                          label="Moon Phases"
                          color="purple"
                        />
                        <StatDisplay
                          icon={Coffee}
                          value={stats.mealsEaten.toLocaleString()}
                          label="Meals Enjoyed"
                          color="orange"
                        />
                        <StatDisplay
                          icon={Calendar}
                          value={`${(stats.pagesRead / stats.pagesInLifeBook * 100).toFixed(1)}%`}
                          label="Life Book Pages"
                          color="green"
                        />
                        <StatDisplay
                          icon={Calendar}
                          value={stats.weeksLived.toLocaleString()}
                          label="Weeks Lived"
                          color="blue"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>

                {/* View Selection */}
                <ViewSelector currentView={view} onViewChange={handleViewChange} />

                {/* Visualization Area */}
                <Card className="min-h-[400px] flex items-center justify-center overflow-hidden p-6 dark:bg-catppuccin-surface0 dark:border-catppuccin-surface1">
                  <AnimatePresence mode="wait" custom={getSlideDirection()}>
                    <motion.div
                      key={view}
                      className="w-full p-2 flex justify-center"
                      custom={getSlideDirection()}
                      variants={pageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      {renderView()}
                    </motion.div>
                  </AnimatePresence>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.footer
            className="text-center text-gray-400 dark:text-catppuccin-overlay1 mt-8 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p>Life expectancy data based on US averages. This is a visualization tool for perspective, not a prediction.</p>
            <p className="mt-2">&copy; {new Date().getFullYear()} Mortal Math</p>
          </motion.footer>
        </div>
      </div>
    </>
  );
};

export default App;
